import {
  CustomMappingType,
  IFailedCustomMappingRecord,
  type ICustomMapping,
  type ICustomMappingSourceOption,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import { Practice, toINamedDocuments } from '@principle-theorem/principle-core';
import { type ITag } from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  IBlobFilenamePair,
  XSLXImporterExporter,
  asyncForEach,
  multiSortBy$,
  nameSorter,
  reduce2DArray,
  snapshot,
  type INamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import { sortBy, uniq } from 'lodash';
import { combineLatest } from 'rxjs';
import { BaseCustomMappingHandler } from '../../../base-custom-mapping-handler';
import { CustomMapping } from '../../../custom-mapping';
import { TranslationMapHandler } from '../../../translation-map';
import { CORE_PRACTICE_MEDIA_CATEGORY_TAG_NAME_MAP } from '../../source/entities/patient-media';
import { PatientMediaCategorySourceEntity } from '../../source/entities/patient-media-categories';
import { MediaCategoriesToXSLX } from './media-categories-to-xlsx';
import { XSLXToMediaCategories } from './xlsx-to-media-categories';
import { PracticeMigration } from '../../../practice-migrations';

const PATIENT_MEDIA_CATEGORY_CUSTOM_MAPPING_TYPE =
  'patientMediaCategoryCustomMapping';

export const MEDIA_CATEGORIES_MAPPING: ICustomMapping = CustomMapping.init({
  metadata: {
    label: 'Media Categories',
    description:
      'This allows us to map the subfolder of a Core Practice file as a tag when migrating to Principle. We first need to copy the patient files into the migration bucket before the source categories can be populated.',
    type: PATIENT_MEDIA_CATEGORY_CUSTOM_MAPPING_TYPE,
  },
  type: CustomMappingType.DocumentReference,
});

export class CorePracticeMediaTagMappingHandler extends BaseCustomMappingHandler<ITag> {
  customMapping = MEDIA_CATEGORIES_MAPPING;

  async getSourceOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<ICustomMappingSourceOption[]> {
    const options = await this.getMediaCategoryOptions(migration);

    return sortBy(
      options.map((name) => ({
        label: name,
        value: name,
      })),
      'label'
    );
  }

  async getDestinationOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<INamedDocument<ITag>[]> {
    return snapshot(
      combineLatest(
        migration.configuration.practices.map((practice) =>
          Practice.mediaTags$(practice)
        )
      ).pipe(reduce2DArray(), toINamedDocuments(), multiSortBy$(nameSorter()))
    );
  }

  async getMappingBlob(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBlobFilenamePair> {
    const { fileName, mediaCategories, translator } =
      await this._getExporterData(migration);

    return new XSLXImporterExporter().getBlob(
      fileName,
      mediaCategories,
      translator
    );
  }

  async downloadMapping(migration: WithRef<IPracticeMigration>): Promise<void> {
    const { fileName, mediaCategories, translator } =
      await this._getExporterData(migration);

    await new XSLXImporterExporter().download(
      fileName,
      mediaCategories,
      translator
    );
  }

  async uploadMapping(
    migration: WithRef<IPracticeMigration>,
    file: File
  ): Promise<void> {
    const items = await new XSLXImporterExporter().parse(
      file,
      new XSLXToMediaCategories()
    );

    const translationMap = new TranslationMapHandler(migration);
    const records = await this.getRecords(translationMap);
    await asyncForEach(records, (record) =>
      translationMap.deleteRecord(record)
    );

    const sourceOptions = await this.getSourceOptions(migration);
    const mediaTags = await this.getDestinationOptions(migration);

    const failedRecords: IFailedCustomMappingRecord[] = [];

    await asyncForEach(items, async (item) => {
      const label = item.category;
      const value = sourceOptions.find(
        (sourceOption) => sourceOption.label === label
      )?.value;
      if (!value) {
        return;
      }

      const tagName = item.mapTo;

      if (!tagName) {
        return;
      }

      const tag = mediaTags.find((mediaTag) => mediaTag.name === tagName);

      if (!tag) {
        // eslint-disable-next-line no-console
        console.error(
          `Mapping error: ${this.customMapping.metadata.label} - Couldn't find tag for item`,
          item
        );

        failedRecords.push({
          sourceIdentifier: value,
          sourceLabel: label,
          reason: `Destination identifier "${tagName.trim()}" not found`,
        });
        return;
      }

      await this.upsertRecord(
        {
          destinationIdentifier: tag.ref,
          sourceIdentifier: value,
          sourceLabel: label,
        },
        translationMap
      );
    });

    const customMapping = await snapshot(
      PracticeMigration.findCustomMapping$(migration, this.customMapping)
    );
    if (!customMapping) {
      throw new Error('Custom mapping not found for updating failed records');
    }
    await Firestore.patchDoc(customMapping.ref, {
      failedRecords,
    });
  }

  async getMediaCategoryOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<string[]> {
    const entity = new PatientMediaCategorySourceEntity();
    const records = await entity.getAllRecords$(migration).toPromise();

    const options = records.reduce((current: string[], record) => {
      if (!record.data.id) {
        return current;
      }

      const category =
        CORE_PRACTICE_MEDIA_CATEGORY_TAG_NAME_MAP[record.data.id];
      if (!category) {
        return current;
      }

      return uniq([...current, category]);
    }, []);

    return options.sort();
  }

  private async _getExporterData(
    migration: WithRef<IPracticeMigration>
  ): Promise<{
    fileName: string;
    mediaCategories: string[];
    translator: MediaCategoriesToXSLX;
  }> {
    const mediaTags = await this.getDestinationOptions(migration);
    const fileName = this.getFileName();
    const mediaCategories = await this.getMediaCategoryOptions(migration);
    const translationMap = new TranslationMapHandler(migration);

    const translator = new MediaCategoriesToXSLX(
      mediaTags,
      await this.getRecords(translationMap)
    );
    return { fileName, mediaCategories, translator };
  }
}
